import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import {
  RentObjectsProps,
  RentObject,
} from '@features/effector-form/controls/rent-objects/types';
import { withControl } from '@features/effector-form/hoc/withControl';
import { RentObjectsControlGate, $data, $pending } from './model';

export const RentObjectsControl = withControl<RentObjectsProps>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    propertyType = null,
    object = null,
    excludeOptions = [],
    placeholder = '',
    isError,
    availableForBooking,
  }) => {
    useGate(RentObjectsControlGate, {
      propertyType: propertyType as string,
      object: object as string | string[],
      availableForBooking,
    });

    const [data, pending] = useUnit([$data, $pending]);

    const parentGuids = Array.isArray(object) ? object.join('-') : object;

    const optionKey =
      object && propertyType ? `${parentGuids as string}-${propertyType}` : null;

    const options = optionKey ? (data[optionKey] ?? []) : [];
    const val = value !== '' ? options?.find((item) => item.id === value) : null;

    const handleFilterOptions = (
      opts: RentObject[],
      { inputValue }: { inputValue: string }
    ) => {
      const currentOptions = excludeOptions.length
        ? opts.reduce(
            (acc: RentObject[], option) =>
              !excludeOptions.includes(option.id) ? [...acc, option] : acc,
            []
          )
        : opts;

      if (inputValue) {
        return currentOptions.filter((opt) =>
          opt?.label?.toUpperCase().includes(inputValue.toUpperCase())
        );
      }

      return currentOptions;
    };

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          filterOptions={handleFilterOptions}
          loading={pending}
          value={!data ? null : val || null}
          options={options && propertyType && object ? options : []}
          onChange={(_: unknown, newValue: any) => {
            setValue(newValue?.id || null);
          }}
          ListboxComponent={Listbox}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                readOnly,
                placeholder,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
